<template>
  <div class="main-container" :key="activeStep">
    <SidebarNav @update:sidebarNavCollapsed="sidebarNavCollapsed = $event" />
    <div class="onboarding-status-container" :class="{['onboarding-status-container-sidebar-collapse']: sidebarNavCollapsed}">
      <div class="onboarding-status-sub-container">
        <div class="header">
          <div class="table-header">
            {{ onboardingStatus === "APPROVED" ? "You’re onboarded to Chargebee Pay! 🎉": "Set up Chargebee Pay" }}
          </div>

          <div class="table-header-description">
            Check back here to track progress. We'll also send you updates via email.
          </div>
        </div>

        <div class="summary-container">
          <div class="milestones">
            <div class="milestones-header">
              Milestones
            </div>
          </div>
          <s-progress-bar
            variant="success"
            :infinite="false"
            :value-min="0"
            :value-max="steps.length"
            :value-now="activeStep-1"
          />
          <div style="width: 100%; padding-bottom: 18px;">
            <s-accordion
              v-for="step in steps"
              :key="step.id"
              :all-expanded="false"
              :multiple="false"
              :toggle-display-right="false"
              :toggle-display-left="false"
            >
              <s-accordion-item
                :item-id="step.id"
                :toggle-display-right="false"
                style="padding: 12px 0 0 12px"
                :disabled="isStepDisabled(step.id)"
              >
                <div
                  slot="header" :style="{
                    display: 'flex',
                    cursor: !isStepDisabled(step.id) ? 'pointer' : 'not-allowed',
                    padding: '0 0 0 16px',
                  }"
                >
                  <div class="id" slot="header" v-if="step.failed === true">
                    <c-icon name="failure" size="24" style="color: #de1827" />
                  </div>
                  <div class="id" slot="header" v-else-if="!step.completed">
                    <NumberIcon :number="step.id" />
                  </div>
                  <div class="id" slot="header" v-else>
                    <s-icon name="check-circle" size="24" style="color: #50b83c" />
                  </div>
                  <div class="steps-title">
                    {{ step.title }}
                  </div>
                </div>
                <div slot="content" style="padding-left: 60px; display: flex">
                  <div style="width: 90%">
                    <component :is="step.component" v-bind="step.componentProps || {}" v-on="step.events || {}" />
                  </div>
                </div>
              </s-accordion-item>
            </s-accordion>
          </div>
        </div>
        <i18n path="onboarding.assistance" tag="div" class="title-description s-leading-snug">
          <template #faqPage>
            <c-link
              href="https://www.chargebee.com/docs/2.0/cb_payments_faq.html"
              target="_blank"
            >
              {{ $t('onboarding.faqPage') }}
            </c-link>
          </template>
          <template #documentationPage>
            <c-link
              href="https://www.chargebee.com/docs/2.0/cb_payments.html"
              target="_blank"
            >
              {{ $t('onboarding.documentationPage') }}
            </c-link>
          </template>
        </i18n>
      </div>
    </div>
    <AppPreLoader
      :show-pre-loader="showLoader"
      :loading-message="loadingMessage"
    />
  </div>
</template>

<script>
import SidebarNav from "@/components/SidebarNav.vue";
import ApplicationPending from "@/views/onboarding-status/ApplicationPending.vue";
import NumberIcon from "@/components/icons/NumberIcon.vue";
import ApplicationInReview from "@/views/onboarding-status/ApplicationInReview.vue";
import { mapGetters } from "vuex";
import { CbStorage, ONBOARDING_SUBMITTED_STEP, OnboardingStatus, OnboardingStep } from "@/app/utils/common/constants";
import EnablePaymentSettings from "@/views/onboarding-status/EnablePaymentSettings.vue";
import cbStorage from "@/app/utils/common/core/cb-storage";
import AddBankAccount from "@/views/onboarding-status/AddBankAccount.vue";
import {FailureReasonCategory} from "@/api/paymentApi";
import { navigateTo } from "@/router";
import { RouteConstants } from "@/router/routeConstants";
import AppPreLoader from "@/components/common/AppPreLoader.vue";
import { getAndStoreMerchantAndConfiguration } from "@/service/sessionService";

export default {
  name: "SetupProgress",
  components: { AppPreLoader, NumberIcon, SidebarNav },
  data() {
    return {
      sidebarNavCollapsed: false,
      windowWidth: window.innerWidth,
      onboardingStatus: null,
      onboardingStep: null,
      activeStep: 0,
      showLoader: false,
      loadingMessage: null,
    };
  },
  computed: {
    ...mapGetters('app', ['getSessionPayfac', 'getMerchantId', 'isAdminMode']),
    ...mapGetters('merchantPayfacDetails', ['getOnboardingStatus', 'getFailureReasonCategory','getOnboardingStep']),
    steps() {
      let stepsArr = [
        {
          title: 'Submit business details',
          id: 1,
          component: this.activeStep === 1 && ApplicationPending,
          completed: ONBOARDING_SUBMITTED_STEP.includes(this.onboardingStep),
        },
        {
          title: 'Application Review',
          id: 2,
          component: this.activeStep === 2 && ApplicationInReview,
          componentProps: { onboardingStatus: this.onboardingStatus, failureReasonCategory: this.getFailureReasonCategory(this.getSessionPayfac) },
          completed: this.onboardingStatus === OnboardingStatus.APPROVED || this.onboardingStep === OnboardingStep.ADDED_BANK_ACCOUNT,
          failed: this.onboardingStatus === OnboardingStatus.REJECTED && this.getFailureReasonCategory(this.getSessionPayfac) === FailureReasonCategory.DECLINED
        },
        {
          title: 'Configure payment settings',
          id: 3,
          component: this.activeStep === 3 && EnablePaymentSettings,
          componentProps: {},
          completed: cbStorage.getItem(CbStorage.OnboardingBillingConfiguration) === "true",
          events: {
            handleMarkAsDone: () => this.markBillingConfigurationAsDone(),
          },
        }
      ];
      if ([OnboardingStep.SUBMITTED_SKIPPED_BANK_ACCOUNT, OnboardingStep.ADDED_BANK_ACCOUNT].includes(this.onboardingStep)) {
        let bankAccount = {
          title: 'Add bank account',
          id: 4,
          component: this.activeStep === 4 && AddBankAccount,
          componentProps: { onboardingStatus: this.onboardingStatus},
          completed: this.onboardingStatus === OnboardingStatus.APPROVED && this.onboardingStep === OnboardingStep.ADDED_BANK_ACCOUNT,
        }
        stepsArr.push(bankAccount);
      }
      return stepsArr;
    }
  },
  async mounted() {
    if (this.isAdminMode && !this.getSessionPayfac) {
      await getAndStoreMerchantAndConfiguration(this.getMerchantId);
    }
    this.onboardingStatus = this.getOnboardingStatus(this.getSessionPayfac);
    this.onboardingStep = this.getOnboardingStep(this.getSessionPayfac);

    if (this.onboardingStep === OnboardingStep.NOT_INITIATED) {
      navigateTo(RouteConstants.INCREMENTAL_ONBOARDING);
      return;
    }
    this.setSteps();
    if (this.activeStep === 0) {
      this.navigateToDashboard(5000, "Onboarding completed");
    }
  },
  methods: {
    markBillingConfigurationAsDone() {
      // Update local storage
      cbStorage.setItem(CbStorage.OnboardingBillingConfiguration, true);
      this.steps.filter(step => step.id === 3)[0].completed = true;
      this.setSteps();
      const stepLength = this.steps.length;
      if (this.activeStep === stepLength && this.steps[stepLength - 1].completed === true) {
        this.navigateToDashboard(2000, "Opening CB Pay experience");
      }
    },
    setSteps() {
      for (let i = 0; i < this.steps.length; i++) {
        if (this.steps[i].completed === false) {
          this.activeStep = i+1;
          return;
        }
      }
    },
    isStepDisabled(stepId) {
      return stepId !== this.activeStep;
    },
    navigateToDashboard(time, loadingMessage) {
      this.showLoader = true;
      this.loadingMessage = loadingMessage || "Opening CB Pay experience";
      setTimeout(() => {
        navigateTo(RouteConstants.DASHBOARD);
        this.showLoader = false;
      }, time);
    }
  },
};
</script>

<style lang="scss">

.main-container {
  background: $neutral_50;
  height: 100%;
  overflow: scroll;
}

.onboarding-status-container {
  left: 256px;
  top: 0;
  position: relative;
  width: calc(100% - 256px);
  padding: 20px 30px 30px 30px;
  transition: all 0.2s ease-in;
  justify-content: center;
  display: flex;

  & .onboarding-status-sub-container {
    width: 80%;
    max-width: 1360px;

    & .title-description {
      text-align: left;
      margin-top: 20px;
      font-size: 14px;
      font-weight: $weight_normal;
    }
  }

  & .onboarding-status-container-sidebar-collapse {
    left: 86px;
    width: calc(100% - 86px);
  }

  & .empty-placeholder-icon {
    display: flex;
    width: 40px;
    height: 40px;
  }

  & .table-header {
    font-style: normal;
    font-weight: $fw_bold;
    font-size: 20px;
    line-height: 30px;
    color: $neutral_1000;
    text-align: left;
    padding: 20px 0 0 0;
  }

  & .table-header-description {
    font-style: normal;
    font-weight: $fw_normal;
    font-size: 12px;
    line-height: 18px;
    color: $neutral_600;
    text-align: left;
    padding: 8px 0 24px 0;
  }

  .summary-container {
    background-color: $color_white;
    width: 100%;
    height: fit-content;
    border: 1px solid $neutral_100;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .milestones {
      width: 100%;
      height: 56px;
      background: #EAECEE;
      border-radius: 8px 8px 0 0;
      padding: 16px;

      & .milestones-header {
        font-style: normal;
        font-weight: $weight_bold;
        font-size: 16px;
        line-height: 30px;
        color: $neutral_1000;
        display: flex;
        justify-content: space-between;
      }
    }

    .s-top-0 {
      top: unset !important;
    }

    .s-absolute {
      position: unset !important;
    }

      .id {
        font-weight: $weight_normal;
        color: $neutral_1000;
        width: 45px
      }


      .steps-title {
        font-style: normal;
        font-weight: $weight_medium;
        font-size: 16px;
        line-height: 24px;
        color: $neutral_1000;
        text-align: left;
      }


  }
}

.s-bg-neutral-50 {
  background-color: $base_white !important;
}

.s-border-neutral-100 {
  border-color: $base_white !important;
}

.s-bg-green-400 {
  background-color: $successa_fill !important;
}

.s-h-1 {
  height: 0.4rem !important;
}

.s-p-3 {
  padding-top: 0 !important;
}

</style>